import React, { useEffect, useState } from 'react';
import {
  Button,
  Modal,
  Form,
  Input,
  Icon,
  Segment,
  Loader,
  Accordion,
  Card,
  Label,
  Table,
  Dropdown,
} from 'semantic-ui-react';
import { toast } from 'react-toastify';
import {
  getSuperStaffCategoryHierarchy,
  createSuperStaffCategory,
  updateSuperStaffCategory,
  deleteSuperStaffCategory,
  createStaffCategoryAndAddToSuper,
  addStaffCategoryToSuper,
  removeStaffCategoryFromSuper,
} from '../../services/superStaffCategoryService';
import {
  getAllStaffCategories,
} from '../../services/staffCategoryService';
import { updateCustomerAdmin } from '../../services/customerService';
import './StaffCategories.css';

const StaffCategories = () => {
  const [superCategories, setSuperCategories] = useState([]);
  const [allStaffCategories, setAllStaffCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuperModal, setShowSuperModal] = useState(false);
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [showAddExistingModal, setShowAddExistingModal] = useState(false);
  const [isEditingSuper, setIsEditingSuper] = useState(false);
  const [superFormData, setSuperFormData] = useState({ category: '' });
  const [staffFormData, setStaffFormData] = useState({ category: '' });
  const [editingSuperCategoryId, setEditingSuperCategoryId] = useState(null);
  const [currentSuperCategoryId, setCurrentSuperCategoryId] = useState(null);
  const [selectedStaffCategory, setSelectedStaffCategory] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [categoryCustomers, setCategoryCustomers] = useState({});
  const [expandedStaffCategories, setExpandedStaffCategories] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    
    const hierarchyResult = await getSuperStaffCategoryHierarchy();
    const staffResult = await getAllStaffCategories();

    setIsLoading(false);

    if (hierarchyResult.errors) {
      toast.error('Failed to load staff categories hierarchy');
      console.error(hierarchyResult.errors);
    } else if (hierarchyResult.data) {
      const sorted = Array.isArray(hierarchyResult.data)
        ? hierarchyResult.data.sort((a, b) => (a.category > b.category ? 1 : -1))
        : [];
      
      // Pre-populate categoryCustomers from hierarchy data
      const customersMap = {};
      sorted.forEach((superCategory) => {
        if (superCategory.staffCategories && Array.isArray(superCategory.staffCategories)) {
          superCategory.staffCategories.forEach((staffCategory) => {
            if (staffCategory.customers) {
              customersMap[staffCategory.staffCategoryId] = staffCategory.customers;
            }
          });
        }
      });
      
      setCategoryCustomers(customersMap);
      setSuperCategories(sorted);
    }

    if (staffResult.data) {
      setAllStaffCategories(
        Array.isArray(staffResult.data) ? staffResult.data : []
      );
    }
  };

  const handleOpenSuperModal = () => {
    setIsEditingSuper(false);
    setEditingSuperCategoryId(null);
    setSuperFormData({ category: '' });
    setShowSuperModal(true);
  };

  const handleEditSuperClick = (superCategory) => {
    setIsEditingSuper(true);
    setEditingSuperCategoryId(superCategory.superStaffCategoryId);
    setSuperFormData({ category: superCategory.category });
    setShowSuperModal(true);
  };

  const handleCloseSuperModal = () => {
    setShowSuperModal(false);
    setIsEditingSuper(false);
    setEditingSuperCategoryId(null);
    setSuperFormData({ category: '' });
  };

  const handleOpenStaffModal = (superCategoryId) => {
    setCurrentSuperCategoryId(superCategoryId);
    setStaffFormData({ category: '' });
    setShowStaffModal(true);
  };

  const handleCloseStaffModal = () => {
    setShowStaffModal(false);
    setStaffFormData({ category: '' });
    setCurrentSuperCategoryId(null);
  };

  const handleOpenAddExistingModal = (superCategoryId) => {
    setCurrentSuperCategoryId(superCategoryId);
    setSelectedStaffCategory(null);
    setShowAddExistingModal(true);
  };

  const handleCloseAddExistingModal = () => {
    setShowAddExistingModal(false);
    setSelectedStaffCategory(null);
    setCurrentSuperCategoryId(null);
  };

  const handleSaveSuper = async () => {
    if (!superFormData.category.trim()) {
      toast.warning('Please enter a parent category name');
      return;
    }

    setIsSaving(true);
    let result;

    if (isEditingSuper) {
      result = await updateSuperStaffCategory(
        editingSuperCategoryId,
        superFormData.category
      );
    } else {
      result = await createSuperStaffCategory(superFormData.category);
    }

    setIsSaving(false);

    if (result.errors) {
      toast.error(
        isEditingSuper
          ? 'Failed to update parent category'
          : 'Failed to create parent category'
      );
      console.error(result.errors);
      return;
    }

    toast.success(
      isEditingSuper
        ? 'Parent category updated successfully'
        : 'Parent category created successfully'
    );
    handleCloseSuperModal();
    await loadData();
  };

  const handleSaveStaffCategory = async () => {
    if (!staffFormData.category.trim()) {
      toast.warning('Please enter a category name');
      return;
    }

    setIsSaving(true);
    const result = await createStaffCategoryAndAddToSuper(
      currentSuperCategoryId,
      staffFormData.category
    );
    setIsSaving(false);

    if (result.errors) {
      toast.error('Failed to create and add staff category');
      console.error(result.errors);
      return;
    }

    toast.success('Staff category created and added successfully');
    handleCloseStaffModal();
    await loadData();
  };

  const handleAddExistingStaffCategory = async () => {
    if (!selectedStaffCategory) {
      toast.warning('Please select a staff category');
      return;
    }

    setIsSaving(true);
    const result = await addStaffCategoryToSuper(
      currentSuperCategoryId,
      selectedStaffCategory
    );
    setIsSaving(false);

    if (result.errors) {
      toast.error('Failed to add staff category to parent category');
      console.error(result.errors);
      return;
    }

    toast.success('Staff category added successfully');
    handleCloseAddExistingModal();
    await loadData();
  };

  const handleDeleteSuper = async (superCategoryId) => {
    if (window.confirm('Delete this parent category? This will not delete staff categories.')) {
      const result = await deleteSuperStaffCategory(superCategoryId);

      if (result.errors) {
        toast.error('Failed to delete parent category');
        console.error(result.errors);
        return;
      }

      toast.success('Parent category deleted successfully');
      await loadData();
    }
  };

  const handleRemoveStaffCategory = async (
    superCategoryId,
    staffCategoryId,
    superCategory
  ) => {
    if (window.confirm('Remove this staff category from the parent category?')) {
      const result = await removeStaffCategoryFromSuper(
        superCategoryId,
        staffCategoryId
      );

      if (result.errors) {
        toast.error('Failed to remove staff category');
        console.error(result.errors);
        return;
      }

      toast.success('Staff category removed from parent category');
      await loadData();
    }
  };

  const handleExpandStaffCategory = async (staffCategoryId) => {
    const isExpanded = expandedStaffCategories[staffCategoryId];
    
    setExpandedStaffCategories({
      ...expandedStaffCategories,
      [staffCategoryId]: !isExpanded,
    });
  };

  const handleRemoveCustomerFromCategory = async (
    customerId,
    staffCategoryId,
    customer
  ) => {
    if (window.confirm('Remove this customer from the category?')) {
      const updatedStaffCategoryIds = customer.staffCategoryIds.filter(
        (id) => id !== staffCategoryId
      );
      
      const result = await updateCustomerAdmin({
        customerId: customer.customerId,
        staffCategoryIds: updatedStaffCategoryIds,
      });

      if (result.errors) {
        toast.error('Failed to remove customer from category');
        console.error(result.errors);
        return;
      }

      toast.success('Customer removed from category');
      
      // Update the local state
      const updatedCustomers = categoryCustomers[staffCategoryId].map(c =>
        c.customerId === customerId
          ? { ...c, staffCategoryIds: updatedStaffCategoryIds }
          : c
      ).filter(c => c.staffCategoryIds.includes(staffCategoryId));
      
      setCategoryCustomers({
        ...categoryCustomers,
        [staffCategoryId]: updatedCustomers,
      });
    }
  };

  const getAvailableStaffCategories = (superCategory) => {
    const assignedIds = new Set(
      (superCategory.staffCategories || []).map((sc) => sc.staffCategoryId)
    );
    return allStaffCategories.filter((sc) => !assignedIds.has(sc.staffCategoryId));
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Loader active inline='centered'>
          Loading staff categories...
        </Loader>
      </div>
    );
  }

  return (
    <div className='staff-categories-container'>
      <Segment>
        <div className='staff-categories-header'>
          <h1 className='title'>Staff Categories</h1>
          <Button
            primary
            onClick={handleOpenSuperModal}
            icon
            labelPosition='left'
          >
            <Icon name='plus' />
            Add Parent Category
          </Button>
        </div>

        {superCategories.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <p>No super staff categories found. Create one to get started.</p>
          </div>
        ) : (
          <div className='super-categories-list'>
            {superCategories.map((superCategory) => (
              <Card key={superCategory.superStaffCategoryId} fluid>
                <Card.Content>
                  <div className='super-category-header'>
                    <div>
                      <Card.Header>{superCategory.category}</Card.Header>
                    </div>
                    <div className='super-category-actions'>
                      <Button
                        icon
                        color='blue'
                        size='small'
                        onClick={() => handleEditSuperClick(superCategory)}
                        title='Edit'
                      >
                        <Icon name='edit' />
                      </Button>
                      <Button
                        icon
                        color='red'
                        size='small'
                        onClick={() =>
                          handleDeleteSuper(superCategory.superStaffCategoryId)
                        }
                        title='Delete'
                      >
                        <Icon name='trash' />
                      </Button>
                    </div>
                  </div>

                  {(!superCategory.staffCategories ||
                    superCategory.staffCategories.length === 0) ? (
                    <div style={{ marginTop: '10px', color: '#999' }}>
                      No staff categories
                    </div>
                  ) : (
                    <div style={{ marginTop: '15px' }}>
                      {superCategory.staffCategories.map((staffCategory) => (
                        <div
                          key={staffCategory.staffCategoryId}
                          className='staff-category-item'
                        >
                          <div className='staff-category-header'>
                            <Label>
                              {staffCategory.category}
                              <Label.Detail>
                                {
                                  (
                                    categoryCustomers[
                                      staffCategory.staffCategoryId
                                    ] || []
                                  ).length
                                }{' '}
                                customers
                              </Label.Detail>
                            </Label>
                            <div className='staff-category-buttons'>
                              <Button
                                icon
                                size='mini'
                                onClick={() =>
                                  handleExpandStaffCategory(
                                    staffCategory.staffCategoryId
                                  )
                                }
                              >
                                <Icon
                                  name={
                                    expandedStaffCategories[
                                      staffCategory.staffCategoryId
                                    ]
                                      ? 'chevron up'
                                      : 'chevron down'
                                  }
                                />
                              </Button>
                              <Button
                                icon
                                color='red'
                                size='mini'
                                onClick={() =>
                                  handleRemoveStaffCategory(
                                    superCategory.superStaffCategoryId,
                                    staffCategory.staffCategoryId,
                                    superCategory
                                  )
                                }
                                title='Remove from parent category'
                              >
                                <Icon name='trash' />
                              </Button>
                            </div>
                          </div>

                          {expandedStaffCategories[
                            staffCategory.staffCategoryId
                          ] && (
                            <div className='staff-category-customers'>
                              {(categoryCustomers[
                                staffCategory.staffCategoryId
                              ] || []).length === 0 ? (
                                <div style={{ padding: '10px', color: '#999' }}>
                                  No customers
                                </div>
                              ) : (
                                <Table size='small' compact>
                                  <Table.Body>
                                    {(
                                      categoryCustomers[
                                        staffCategory.staffCategoryId
                                      ] || []
                                    ).map((customer) => (
                                      <Table.Row
                                        key={customer.customerId}
                                      >
                                        <Table.Cell>
                                          {customer.firstNames}{' '}
                                          {customer.lastName}
                                        </Table.Cell>
                                        <Table.Cell textAlign='right'>
                                          <Button
                                            icon
                                            color='red'
                                            size='mini'
                                            onClick={() =>
                                              handleRemoveCustomerFromCategory(
                                                customer.customerId,
                                                staffCategory.staffCategoryId,
                                                customer
                                              )
                                            }
                                            title='Remove from category'
                                          >
                                            <Icon name='trash' />
                                          </Button>
                                        </Table.Cell>
                                      </Table.Row>
                                    ))}
                                  </Table.Body>
                                </Table>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <div className='staff-category-add-buttons'>
                    <Button
                      secondary
                      size='small'
                      onClick={() =>
                        handleOpenStaffModal(superCategory.superStaffCategoryId)
                      }
                    >
                      Create New Category
                    </Button>
                    <Button
                      secondary
                      size='small'
                      onClick={() =>
                        handleOpenAddExistingModal(
                          superCategory.superStaffCategoryId
                        )
                      }
                      disabled={
                        getAvailableStaffCategories(superCategory).length === 0
                      }
                    >
                      Add Existing Category
                    </Button>
                  </div>
                </Card.Content>
              </Card>
            ))}
          </div>
        )}
      </Segment>

      {/* Parent Category Modal */}
      <Modal open={showSuperModal} onClose={handleCloseSuperModal} size='small'>
        <Modal.Header>
          {isEditingSuper
            ? 'Edit Parent Category'
            : 'Create New Parent Category'}
        </Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Field>
              <label>Category Name</label>
              <Input
                placeholder='Enter parent category name'
                value={superFormData.category}
                onChange={(e) =>
                  setSuperFormData({ category: e.target.value })
                }
                autoFocus
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleCloseSuperModal}>Cancel</Button>
          <Button
            primary
            onClick={handleSaveSuper}
            loading={isSaving}
            disabled={isSaving}
          >
            {isEditingSuper ? 'Update' : 'Create'}
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Staff Category Modal */}
      <Modal open={showStaffModal} onClose={handleCloseStaffModal} size='small'>
        <Modal.Header>Create New Staff Category</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Field>
              <label>Category Name</label>
              <Input
                placeholder='Enter staff category name'
                value={staffFormData.category}
                onChange={(e) =>
                  setStaffFormData({ category: e.target.value })
                }
                autoFocus
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleCloseStaffModal}>Cancel</Button>
          <Button
            primary
            onClick={handleSaveStaffCategory}
            loading={isSaving}
            disabled={isSaving}
          >
            Create & Add
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Add Existing Category Modal */}
      <Modal
        open={showAddExistingModal}
        onClose={handleCloseAddExistingModal}
        size='small'
      >
        <Modal.Header>Add Existing Staff Category</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Field>
              <label>Select a Category</label>
              <Dropdown
                placeholder='Select a staff category'
                fluid
                search
                selection
                options={getAvailableStaffCategories(
                  superCategories.find(
                    (sc) =>
                      sc.superStaffCategoryId === currentSuperCategoryId
                  ) || {}
                ).map((sc) => ({
                  key: sc.staffCategoryId,
                  text: sc.category,
                  value: sc.staffCategoryId,
                }))}
                value={selectedStaffCategory || ''}
                onChange={(e, { value }) => setSelectedStaffCategory(value)}
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleCloseAddExistingModal}>Cancel</Button>
          <Button
            primary
            onClick={handleAddExistingStaffCategory}
            loading={isSaving}
            disabled={isSaving}
          >
            Add
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default StaffCategories;
